<script lang="ts">
  import {
    getContext,
    onMount,
    type SvelteComponentTyped,
    type ComponentProps,
  } from "svelte"
  import contactIcon from "../Icons/message.svg?raw"
  import testDriveIcon from "../Icons/test_drive.svg?raw"
  import weplyIcon from "../Icons/weply.svg?raw"
  import DrawerItems from "../constants/DrawerItems"
  import DrawerStates from "../constants/DrawerStates"
  import IntegrationTypes from "../constants/IntegrationTypes"
  import ModuleTypes from "../constants/ModuleTypes"
  import PromptTypes from "../constants/PromptTypes"
  import Drawer from "./Drawer.svelte"
  import DrawerItem from "./DrawerItem.svelte"
  import ContactPrompt from "./ContactPrompt.svelte"
  import TradeInPrompt from "./TradeInPrompt.svelte"
  import TestDrivePrompt from "./TestDrivePrompt.svelte"
  import Card from "../components/Card.svelte"
  import {
    buildUrlData,
    type BuildUrlDataOutput,
  } from "../lib/buildModuleUrlData"
  import { scale } from "svelte/transition"
  import LegacyPrompt from "../LegacyPrompt/index.svelte"
  import Close from "../Icons/Close.svg?raw"
  import Svg from "../components/Svg.svelte"
  import { hexToRgba } from "../../lib/helpers"
  import {
    engagementWidgetMinimizedItemName,
    engagementWidgetPromptClosedItemName,
  } from "website_script/constants/SessionStorageItems"
  import { getTestDriveModuleType } from "website_script/lib/getTestDriveModuleType"
  import type { Theme } from "website_script/types/theme"
  import type { Texts } from "website_script/types/texts"
  import type { Settings } from "website_script/types/settings"
  import type {
    PromptComponentOnModalTriggerFunc,
    PromptComponentOnModalTriggerOpts,
  } from "./types"
  import type { Environment } from "website_script/types/environment"
  import { leadFlowIcon } from "conversion_flows/types/leadFlowIcons"
  import { EVENT_TYPE, FLOW } from "website_script/lib/events"

  // Contexts
  const { abTesting, mobile, timesLoaded, trackFunction } =
    getContext<Environment>("environment")
  const settings = getContext<Settings>("settings")
  const texts = getContext<Texts>("texts")
  const { widgetBottomOffset, leftAlign, neutralTextColor, accentColor } =
    getContext<Theme>("theme")

  let testDriveModuleType:
    | ModuleTypes.IMPROVED_BOOK_TEST_DRIVE
    | ModuleTypes.BOOK_TEST_DRIVE
  $: testDriveModuleType = getTestDriveModuleType(settings)

  /** Trigger for the root modal */
  export let onModalTrigger: (urlBuildData: BuildUrlDataOutput) => void
  let drawer: Drawer
  let urlBuildData: BuildUrlDataOutput

  // The engagement widgets have a lot of buttons,
  // if we are showing a lot of them, the user should be able to collapse it
  let numberOfItemsBeforeCollapsable = 3

  const promptsOrder: DrawerItems[] = [
    DrawerItems.TRADE_IN,
    DrawerItems.TEST_DRIVE,
  ]
  // If the user haven't already at some point closed the prompt, show it
  let shouldPromptUser: boolean =
    window.sessionStorage.getItem(engagementWidgetPromptClosedItemName) !==
    "true"
  let shouldMinimizeDrawer: boolean =
    window.sessionStorage.getItem(engagementWidgetMinimizedItemName) === "true"
  /** Array of prompts that should be automatically shown in the widget */
  let promptsInOrder: DrawerItems[]
  let promptInterval: NodeJS.Timeout

  /** currently opened DrawerItem */
  let openedItem: DrawerItems | null = null

  /**
   * Handles triggering of the modal from inside the engagment widget
   * Builds data for the modal url using most recent settings
   */
  const handleModalTrigger = (args: {
    moduleType: ModuleTypes
    source: string
    vin_lpn?: string | undefined
  }) => {
    const { moduleType, source, ...opts } = args
    const urlBuildData = buildUrlData({
      settings,
      abTesting: $abTesting,
      moduleType,
      integrationType: IntegrationTypes.ENGAGEMENT_WIDGET,
    })
    openModalAndCloseWidget({ ...urlBuildData, ...opts })
  }

  /**
   * Open modal based on the urlBuildData, track opening modal from source
   */
  const openModalAndCloseWidget = (urlBuildData: BuildUrlDataOutput) => {
    onModalTrigger(urlBuildData)
    drawer.transitionTo(DrawerStates.COLLAPSED)
    // Closes the widget prompt if any present
    closePromptAndSetHideDrawer(false)
  }

  /**
   * Temporarily sets openedItem to null
   * Opens prompt item after a delay to ensure the show/hide animation for the prompts
   */
  const openDrawerItem = (item: DrawerItems) => {
    openedItem = null
    setTimeout(() => {
      openedItem = item
    }, 150)
  }

  /**
   * Find currently opened item, sets openedItem to '' temporarily
   * Opens next prompt in line with a delay to ensure the animation is visible
   */
  const startPrompting = () => {
    const indexOfItemOpened = promptsInOrder.findIndex((i) => i === openedItem)

    openedItem = null
    setTimeout(() => {
      if (indexOfItemOpened + 1 === promptsInOrder.length) {
        openedItem = promptsInOrder[0] || null
      } else {
        openedItem = promptsInOrder[indexOfItemOpened + 1] || null
      }
    }, 150)
  }

  /**
   * Opens the first prompt on page load,
   * Sets the promptInterval, and starts showing prompt according to set time interval
   */
  const startPromptingInterval = () => {
    if (promptsInOrder[0] === DrawerItems.TRADE_IN) {
      trackFunction({
        entity: "engagement-widget",
        element: "prompt: trade_in",
        action: "displayed",
        type: "displayed_cta",
        flow: "trade_in",
      })
    } else if (promptsInOrder.length) {
      trackFunction(
        `Opened ${promptsInOrder[0]} prompt on widget load`,
        EVENT_TYPE.DISPLAYED_CTA,
        "engagement_widget"
      )
    }
    startPrompting()
    promptInterval = setInterval(startPrompting, 10000)
  }

  /**
   * Assigns url based on what type of module the Modal should open,
   * Opens the module
   */
  const handleClickOnDrawerItem = (
    item: DrawerItems,
    moduleType?: ModuleTypes
  ) => {
    // Clear prompt interval on interaction with any FAB
    clearInterval(promptInterval)
    const widget = widgetOptions[item]
    if (
      widget &&
      !moduleType &&
      typeof widget.promptComponent !== "undefined"
    ) {
      // If there is a promptComponent and module cannot be opened directly, open the prompt
      openDrawerItem(item)
    }
    if (moduleType === ModuleTypes.TRADE_IN) {
      trackFunction({
        entity: "engagement-widget",
        element: "trade_in",
        action: "clicked",
        type: "interacted_with_cta",
        flow: "trade_in",
      })
    } else {
      trackFunction(
        `Clicked Drawer Button - ${item}`,
        EVENT_TYPE.INTERACTED_WITH_CTA,
        "engagement_widget"
      )
    }

    if (moduleType) {
      urlBuildData = buildUrlData({
        settings,
        moduleType,
        abTesting: $abTesting,
        integrationType: IntegrationTypes.ENGAGEMENT_WIDGET,
      })
      openModalAndCloseWidget(urlBuildData)
    }

    switch (item) {
      case DrawerItems.MESSAGE:
        const whereToOpen = settings.openContactPageInNewTab
          ? "_blank"
          : "_self"
        window.open(settings.contactPageUrl, whereToOpen)
        break
      case DrawerItems.CALL:
        window.open(`tel:${settings.contactPhoneNumber}`)
        break
      case DrawerItems.WEPLY:
        window.$$weply.api.publicApi.widget.maximize()
        if (!mobile) {
          window.$$weply.api.publicApi.widget.setOffset(0, 0, 100, 100)
        }
        break
    }
  }

  /**
   * Closes the prompt and opens/hides the drawer
   * A delay is added so that the animations do not combine in a strange way
   */
  const closePromptAndSetHideDrawer = (shouldHideDrawer: boolean) => {
    openedItem = null
    setTimeout(() => {
      hideDrawer = shouldHideDrawer
    }, 350)
  }

  /**
   * Close prompt, track closing
   */
  const handlePromptClose = (
    item?: DrawerItems | null,
    moduleType?: ModuleTypes | null
  ) => {
    // Clear prompt interval on interaction with any FAB
    clearInterval(promptInterval)
    if (moduleType === ModuleTypes.TRADE_IN) {
      trackFunction({
        entity: "engagement-widget",
        element: "prompt: trade_in",
        action: "closed",
        type: "negative_interaction",
        flow: "trade_in",
      })
    } else {
      trackFunction(
        `Closed ${item} prompt`,
        EVENT_TYPE.NEGATIVE_INTERACTION,
        "engagement_widget"
      )
    }

    // When the option to hide engagement widget entirely on prompt close is true
    // do not add auwsEWExtraWrapperClosed to session storage, so the prompt can be shown on the next page
    if (settings.hideEngagementWidgetOnPromptClose) {
      closePromptAndSetHideDrawer(true)
    } else {
      closePromptAndSetHideDrawer(false)
      // if the user has chosen to close the popup, no need to bother them again...
      window.sessionStorage.setItem(
        engagementWidgetPromptClosedItemName,
        "true"
      )
    }
  }

  /**
   * Stop interval when user hovers over the prompt on desktop
   * Stops the interval on prompt touch on mobile
   */
  const handlePromptMouseEnter = () => {
    clearInterval(promptInterval)
  }

  /**
   * Start interval again when user hovers out of the prompt on desktop
   * and there is more than 1 prompt to display
   */
  const handlePromptMouseLeave = () => {
    if (promptsInOrder.length > 1) {
      clearInterval(promptInterval)
      promptInterval = setInterval(startPrompting, 10000)
    }
  }

  /**
   * Abstracts calculation of whether or not the drawer should be collapsable
   */
  const getCollapsability = (
    maxItems: number,
    widgetOptionsValues: WidgetOption[]
  ): boolean => {
    return (
      maxItems - 1 <
      widgetOptionsValues.filter((value) => {
        return value.enabled
      }).length
    )
  }

  const handleDrawerMinimized = () => {
    window.sessionStorage.setItem(engagementWidgetMinimizedItemName, "true")
  }

  const handleDrawerOpened = () => {
    window.sessionStorage.setItem(engagementWidgetMinimizedItemName, "false")
  }

  /** Which trade-in prompt should be used (legacy vs chat-like) */
  const tradeInPrompt = (
    settings: Settings,
    enabledPrompts: { [key in DrawerItems]?: boolean }
  ) => {
    // Show the legacy prompt only when there are no other prompts enabled
    // and the chatPopupCta is disabled
    return settings.activateChatPopupCta ||
      Object.values(enabledPrompts).filter((val) => val).length > 1
      ? TradeInPrompt
      : LegacyPrompt
  }

  let enabledPrompts: { [key in DrawerItems]?: boolean }
  $: enabledPrompts = {
    [DrawerItems.TEST_DRIVE]:
      settings.enableTestDriveCtas &&
      settings.enableTestDriveButtonInEngagementWidget &&
      settings.enableTestDrivePromptInEngagementWidget,
    [DrawerItems.TRADE_IN]:
      settings.enableTradeInCtas &&
      settings.enableTradeInButtonInEngagementWidget,
  }

  interface WidgetOption {
    /* Props passed to DrawerItem Component for widget */
    props: ComponentProps<DrawerItem>
    /* Indicates whether the widget is enabled in Engagement widget */
    enabled: boolean
    /* Indicates whether the prompt for the widget is enabled */
    enablePrompt?: boolean
    /* Indicates whether the prompt for the widget should be triggered automatically or manually */
    promptType?: PromptTypes
    /* Indicates what module should be opened when interacting with the widget */
    moduleType?: ModuleTypes
    /* Prompt that should show for the widget */
    promptComponent?: typeof SvelteComponentTyped<any, any, any>
    /* Props passed to promptComponent */
    promptComponentProps?: {
      onModalTrigger?: PromptComponentOnModalTriggerFunc
    }
  }

  /**
   * More details about widgetOptions can be found here:
   * https://docs.google.com/document/d/1V26JSN5Znfb5wtqVoS2GtrFz-DDD8VQIUEKyy4aOZXE/edit?usp=sharing
   */
  let widgetOptions: Record<DrawerItems, WidgetOption>

  $: widgetOptions = {
    [DrawerItems.TEST_DRIVE]: {
      props: {
        label: texts.testDriveLabel,
        iconSrc: testDriveIcon as string,
        labelStyle: "small",
      },
      promptType: PromptTypes.AUTOMATIC,
      enabled:
        settings.enableTestDriveCtas &&
        settings.enableTestDriveButtonInEngagementWidget,
      enablePrompt: enabledPrompts[DrawerItems.TEST_DRIVE],
      moduleType: testDriveModuleType,
      promptComponent:
        settings.enableTestDrivePromptInEngagementWidget && TestDrivePrompt,
      promptComponentProps: {
        onModalTrigger: (opts: PromptComponentOnModalTriggerOpts) =>
          handleModalTrigger({
            moduleType: testDriveModuleType,
            ...opts,
          }),
      },
    },
    [DrawerItems.TRADE_IN]: {
      props: {
        label: texts.tradeInLabel,
        iconSrc: leadFlowIcon("car_tag"),
        labelStyle: "small",
      },
      promptType: settings.enableTradeInAutomaticPrompt
        ? PromptTypes.AUTOMATIC
        : PromptTypes.MANUAL,
      moduleType: ModuleTypes.TRADE_IN,
      enabled:
        settings.enableTradeInCtas &&
        settings.enableTradeInButtonInEngagementWidget,
      enablePrompt: enabledPrompts[DrawerItems.TRADE_IN],
      promptComponent: tradeInPrompt(settings, enabledPrompts),
      promptComponentProps: {
        onModalTrigger: (opts: PromptComponentOnModalTriggerOpts) =>
          handleModalTrigger({ moduleType: ModuleTypes.TRADE_IN, ...opts }),
      },
    },
    [DrawerItems.CONTACT]: {
      props: {
        label: texts.contactLabel,
        iconSrc: contactIcon as string,
        labelStyle: "small",
      },
      promptType: PromptTypes.MANUAL,
      enabled:
        settings.enableContactButtonInEngagementWidget &&
        (settings.enableMessage || settings.enableCall),
      enablePrompt:
        settings.enableContactButtonInEngagementWidget &&
        (settings.enableMessage || settings.enableCall),
      promptComponent: ContactPrompt,
      promptComponentProps: {
        phoneNumber: settings.contactPhoneNumber,
        showMessageOption: settings.enableMessage,
        showCallOption: settings.enableCall,
        handleClickOnItem: handleClickOnDrawerItem,
      },
    },
    [DrawerItems.WEPLY]: {
      props: {
        label: texts.weplyLabel,
        iconSrc: weplyIcon as string,
        labelStyle: "small",
      },
      enabled: settings.enableWeplyInEngagementWidget && window.$$weply,
      handleClickOnItem: handleClickOnDrawerItem,
    },
  }

  // Checks whether the widget can be collapsed
  $: isCollapsable = getCollapsability(
    numberOfItemsBeforeCollapsable,
    Object.values(widgetOptions)
  )

  $: drawerItemsKeys = Object.values(DrawerItems)

  // Get number of buttons present in the widget
  $: amountOfButtonsEnabled = drawerItemsKeys.filter(
    (widget) => widgetOptions[widget]?.enabled
  ).length

  // Array of the names of the enabled buttons
  $: listOfEnabledButtonsName = drawerItemsKeys.filter(
    (widget) => widgetOptions[widget]?.enabled
  )

  $: flowButtons = listOfEnabledButtonsName.filter(
    (button) => button === "trade_in" || button === "test_drive"
  )

  $: if (flowButtons.length > 0) {
    trackFunction({
      entity: "engagement-widget",
      action: "displayed",
      type: "displayed_cta",
      flow: flowButtons,
    })
  }

  // We hide the drawer only when there is only one button, and that button has automatic prompting enabled
  $: hideDrawer =
    amountOfButtonsEnabled === 1 &&
    typeof listOfEnabledButtonsName[0] !== "undefined" &&
    widgetOptions[listOfEnabledButtonsName[0]].enablePrompt &&
    widgetOptions[listOfEnabledButtonsName[0]].promptType ===
      PromptTypes.AUTOMATIC &&
    shouldPromptUser

  const bottomOffset = widgetBottomOffset || 0

  onMount(() => {
    /**
     * In order to offset Weply widget on the side of EW once it's loaded, we check if Weply is:
     * enabled to be shown in the Engagement Widget, available on the page,
     * an opened popup chat and displayed on desktop.
     */
    const offsetWeplyWidget = () => {
      if (
        settings.enableWeplyInEngagementWidget &&
        window.$$weply &&
        !window.$$weply.api.publicApi.widget.isMinimized() &&
        !mobile
      ) {
        window.$$weply.api.publicApi.widget.setOffset(0, 0, 100, 100)
      }
    }
    // Call the function to offset Wepply widget
    offsetWeplyWidget()
    // If Weply widget is loaded later than our Engagement Widget, we call it back
    setTimeout(() => {
      offsetWeplyWidget()
    }, 500)

    promptsInOrder = promptsOrder.filter((option) => {
      return (
        widgetOptions[option]?.promptType === PromptTypes.AUTOMATIC &&
        widgetOptions[option]?.enablePrompt
      )
    })

    if (
      shouldPromptUser &&
      drawer.getCurrentWidgetState() !== DrawerStates.COLLAPSED
    ) {
      // Open the drawer item and track the event
      setTimeout(() => {
        // Do not prompt if any item was already clicked
        if (!openedItem) {
          // If there is only one prompt, open it manually,
          // else start prompting
          if (
            promptsInOrder.length === 1 &&
            typeof promptsInOrder[0] !== "undefined"
          ) {
            openDrawerItem(promptsInOrder[0])
          } else {
            startPromptingInterval()
          }
          // Track prompt initiation
          if (promptsInOrder.includes(DrawerItems.TRADE_IN)) {
            trackFunction({
              entity: "engagement-widget",
              element: "prompt: trade-in",
              action: "displayed",
              type: "displayed_cta",
              flow: "trade_in",
            })
          } else if (promptsInOrder.length) {
            trackFunction(
              `Widget loaded, starting animation with prompts: ${promptsInOrder.join(
                ", "
              )}`,
              EVENT_TYPE.DISPLAYED_CTA,
              "engagement_widget"
            )
          }
        }
      }, settings.engagementWidgetPromptDelay * 1000)
    }
  })

  timesLoaded.update((x) => x + 1)

  let hideEngagementWidgetCloseButtonColor = neutralTextColor
  let drawerHiddenManually = false
</script>

<div
  class={`
    auws-engagement-widget
    ${leftAlign ? "auws-left-align" : "auws-right-align"}
    ${hideDrawer ? "auws-hide-drawer" : ""}
  `}
  data-testid="engagement-widget"
  style={`--bottom-offset: ${bottomOffset}px`}
>
  <Drawer
    {handlePromptClose}
    {handleDrawerMinimized}
    {handleDrawerOpened}
    bind:this={drawer}
    {isCollapsable}
    {shouldMinimizeDrawer}
    hide={hideDrawer}
  >
    {#if settings.allowHideEngagementWidgetOnPage}
      <div
        class="close-button-container"
        title={texts.dismissEngagementWidgetTooltip}
        style={`
          --accentColor: ${accentColor};
          --neutral-text-color: ${neutralTextColor};
          --box-shadow-color: ${hexToRgba(neutralTextColor, 0.25)}
        `}
        on:mouseleave={() =>
          (hideEngagementWidgetCloseButtonColor = neutralTextColor)}
        on:mouseenter={() => (hideEngagementWidgetCloseButtonColor = "white")}
        on:click={() => {
          hideDrawer = true
          drawerHiddenManually = true
        }}
      >
        <Svg
          data={Close}
          alt={texts.dismissEngagementWidgetTooltip}
          bind:fill={hideEngagementWidgetCloseButtonColor}
          size="20px"
        />
      </div>
    {/if}
    {#each drawerItemsKeys as drawerItem}
      {#if widgetOptions[drawerItem] && widgetOptions[drawerItem].enabled}
        <DrawerItem
          {...widgetOptions[drawerItem].props}
          on:click={() =>
            handleClickOnDrawerItem(
              drawerItem,
              widgetOptions[drawerItem].moduleType
            )}
          hasPrompt={!!widgetOptions[drawerItem].promptComponent}
          showPrompt={drawerItem === openedItem}
        >
          {#if openedItem === drawerItem && widgetOptions[drawerItem].promptType === PromptTypes.MANUAL}
            <div
              class={`auws-prompt-wrapper ${
                listOfEnabledButtonsName[amountOfButtonsEnabled - 1] ===
                drawerItem
                  ? "auws-bottom-align"
                  : "auws-top-align"
              } ${leftAlign ? "auws-left-align" : "auws-right-align"}`}
              transition:scale={{ x: 1, duration: 500 }}
            >
              <Card
                on:close={() =>
                  handlePromptClose(
                    drawerItem,
                    widgetOptions[drawerItem].moduleType
                  )}
              >
                <!-- Move card outside of drawer item, so that it can be reused -->
                <svelte:component
                  this={widgetOptions[drawerItem].promptComponent}
                  {...widgetOptions[drawerItem].promptComponentProps}
                />
              </Card>
            </div>
          {/if}
        </DrawerItem>
      {/if}
    {/each}
  </Drawer>
  {#if openedItem && widgetOptions[openedItem].promptType === PromptTypes.AUTOMATIC && !!widgetOptions[openedItem].promptComponent && !drawerHiddenManually}
    <div
      class={`auws-prompt-wrapper
      ${
        widgetOptions[openedItem].promptComponent === LegacyPrompt
          ? "auws-legacy"
          : ""
      }
      ${leftAlign ? "auws-left-align" : "auws-right-align"}
      ${amountOfButtonsEnabled < 3 ? "auws-bottom-align" : "auws-top-align"}`}
      transition:scale={{ x: 1, duration: 300 }}
      on:mouseenter={handlePromptMouseEnter}
      on:mouseleave={handlePromptMouseLeave}
      on:touchstart={handlePromptMouseEnter}
    >
      {#if widgetOptions[openedItem].promptComponent !== LegacyPrompt}
        <Card
          on:close={() =>
            handlePromptClose(
              openedItem,
              openedItem && widgetOptions[openedItem].moduleType
            )}
        >
          <svelte:component
            this={widgetOptions[openedItem].promptComponent}
            {...widgetOptions[openedItem].promptComponentProps}
          />
        </Card>
      {:else}
        <svelte:component
          this={LegacyPrompt}
          on:close={() =>
            handlePromptClose(
              openedItem,
              openedItem && widgetOptions[openedItem].moduleType
            )}
          {onModalTrigger}
        />
      {/if}
    </div>
  {/if}
</div>

<style>
  .auws-engagement-widget {
    z-index: 2147483646; /* max value - 1 (less than modal) */
    bottom: var(--bottom-offset);
    line-height: 1.4; /* set line height for consistency */
    /* Set font family for consistency */
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
      Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    position: fixed;
    /* Spacing between elements and between the edges of the screen and elements */
    --small-spacing: 8px;
    --large-spacing: 16px;
    /* Side spacing is large on desktop */
    --side-edge-spacing: var(--large-spacing);
    --bottom-edge-spacing: var(--small-spacing);
  }

  .auws-engagement-widget.auws-right-align {
    right: var(--side-edge-spacing);
  }

  .auws-engagement-widget.auws-left-align {
    left: var(--side-edge-spacing);
  }

  .auws-engagement-widget.auws-hide-drawer {
    /* When the drawer is hidden (e.g. single message version) a bottom edge spacing is added */
    bottom: calc(var(--bottom-offset) + var(--bottom-edge-spacing));
  }

  .auws-prompt-wrapper {
    position: absolute;
    z-index: 9;
    /* Beyond this the prompt looks too stretched out */
    max-width: 280px;
    /* Minimum width for the prompts - accounts for the smallest of screens */
    min-width: 230px;
    /* Add margin to the prompt to align with margin on the drawer item */
    margin-bottom: var(--small-spacing);
  }

  .auws-prompt-wrapper.auws-right-align {
    right: 100%;
    margin-right: var(--side-edge-spacing);
  }

  .auws-prompt-wrapper.auws-left-align {
    left: 100%;
    margin-left: var(--side-edge-spacing);
  }

  .auws-prompt-wrapper.auws-legacy {
    /*
     * Don't restrict the legacy prompt because it has
     * specific width for desktop and mobile
     */
    max-width: none;
  }

  .auws-hide-drawer .auws-prompt-wrapper {
    /* Don't add margin when there is no drawer (buttons) */
    margin: 0;
  }

  .auws-bottom-align {
    bottom: 0px;
  }

  .auws-top-align {
    top: 0px;
  }

  @media (max-width: 641px) {
    .auws-engagement-widget {
      /* Spacing between elements is smaller on small screens */
      --side-edge-spacing: var(--small-spacing);
    }

    .auws-prompt-wrapper:not(.auws-legacy) {
      /**
      * On mobile the width of the prompt should not be bigger than
      * the screen width - eng. widget buttons width - 3 x spacing
      * 1 spacing to the left of the screen
      * 1 spacing to the right of the screen
      * 1 spacing between the prompt and eng. widget
      */
      max-width: calc(100vw - 100% - 3 * var(--side-edge-spacing));
    }
  }

  @media (min-width: 642px) {
    .auws-prompt-wrapper:not(.auws-legacy) {
      /* Occupy the biggest possible space, by still abiding to the max-width rule */
      width: 100vw;
    }
  }

  .close-button-container {
    left: 54px;
    margin-bottom: -8px;
    position: relative;
    border-radius: 50%;
    background: #ffffff;
    width: 20px;
    height: 20px;
    box-shadow: 0px 1px 4px var(--box-shadow-color);
  }

  .close-button-container:hover {
    cursor: pointer;
    background: var(--accentColor);
  }
</style>
